import Phaser from 'phaser';

import {
  TILE_SIZE,
  CAMPAIGN_LEVEL_COUNT,
  COLOR_BACKGROUND,
  COLOR_GRID,
  DEFAULT_LEVEL_URL,
  DEFAULT_PAGE_INDEX,
  FADE_DURATION_MS,
  SHAKE_BASE_INTENSITY,
  SHAKE_DURATION_MS,
  SHAKE_MIN_CELLS,
  SHAKE_SLOPE_PER_CELL_H,
  SHAKE_SLOPE_PER_CELL_V,
} from '../config/feel';
import { Bullet } from '../entities/Bullet';
import { Cannon } from '../entities/Cannon';
import { Gear } from '../entities/Gear';
import { LaserCannon } from '../entities/LaserCannon';
import { CONVEYOR_DIR_DATA_KEY, Player, PlayerState } from '../entities/Player';
import { Portal } from '../entities/Portal';
import { Turret } from '../entities/Turret';
import { validateLevel } from '../../shared/level-format/load';
import type { CardinalDir, ConveyorDir, LevelData, PageData } from '../../shared/level-format/types';
import { postToParent } from '../../embed';
import { conveyorPieceFor, loadSprites } from '../sprites';
import { SOUND_KEYS, SOUND_VOLUMES, loadSounds } from '../sounds';

// Cell-distance pickup struct used for teleport / exit triggers (manual
// distance check, mirroring the no-body coin/key pattern). Half-tile
// AABB threshold so the player only triggers when meaningfully overlapped.
type Trigger = { x: number; y: number; targetPage: number };

// Player wall-hit event payload. axis drives camera shake; surface picks
// the SFX (wall / ceiling → HitWall; floor → LandGround).
type WallHitInfo = {
  axis: 'horizontal' | 'vertical';
  surface: 'wall' | 'ceiling' | 'floor';
  cells: number;
};

// Spike rect layout per direction. Each cell splits into:
//   - A "plate" (wall — blocks the player, mounts the spikes)
//   - A "spike" (hazard — kills the player on overlap)
//   - Air (the open side the spikes point into)
// Coords are fractions of TILE_SIZE, origin at the cell's top-left. The
// plate's solid body is added first; the spike's hazard body sits on top
// (visually + collision-wise). For 'up' the lethal points stick UP into
// the cell's top-air; for 'right' they stick RIGHT, etc.
type Rect = { x: number; y: number; w: number; h: number };
// Plate is half the cell (24px at TILE_SIZE 48). At FLIGHT_SPEED 32
// px/frame, Phaser's arcade-physics shortest-distance separation flips
// direction once the player overshoots more than half the static body's
// thickness in one frame. For player height 48 and movement 32, plates
// thinner than ~16px let a single fast frame put the player on the
// far side, where Phaser then pushes them deeper instead of back. 24px
// has comfortable margin and gives a clean 50/50 split.
// Each timed spike provides its own toggle callback — directional
// spikes flip one teeth visual + its body enable, while spike-blocks
// fan out across 4 teeth visuals and the hazard body. The callback
// abstraction means update()'s timer loop is the same for both.
type TimedSpikeToggle = (firing: boolean) => void;

const SPIKE_LAYOUT: Record<CardinalDir, { plate: Rect; spike: Rect }> = {
  up:    { plate: { x: 0,   y: 0.5, w: 1,   h: 0.5 }, spike: { x: 0,   y: 0,   w: 1,   h: 0.5 } },
  down:  { plate: { x: 0,   y: 0,   w: 1,   h: 0.5 }, spike: { x: 0,   y: 0.5, w: 1,   h: 0.5 } },
  left:  { plate: { x: 0.5, y: 0,   w: 0.5, h: 1   }, spike: { x: 0,   y: 0,   w: 0.5, h: 1   } },
  right: { plate: { x: 0,   y: 0,   w: 0.5, h: 1   }, spike: { x: 0.5, y: 0,   w: 0.5, h: 1   } },
};

// Rotation applied to the spike_1 teeth image per dir. Native art has
// teeth pointing up; rotate clockwise 90° per quarter-turn to face
// right/down/left. The arcade body remains axis-aligned (set explicitly
// in makeSpikeTeeth) since arcade physics has no rotated-body support.
const SPIKE_ROTATION: Record<CardinalDir, number> = {
  up: 0,
  right: Math.PI / 2,
  down: Math.PI,
  left: -Math.PI / 2,
};

const LEVEL_KEY = 'level-default';

// Phase 3: data-driven test scene. Loads the default level JSON in
// preload(), reads page DEFAULT_PAGE_INDEX in create(), builds walls
// from the tile grid, spawns the player at page.spawn. The room is
// centered horizontally in the design viewport so smaller pages
// (Godot's default page is 25x20 = 1200x960) sit cleanly inside the
// 1600x960 design space without ugly empty bars on one side.
//
// Optional element arrays (spikes, gears, portals, …) are present in the
// loaded JSON but not yet consumed — those land in Phases 4+.
export class PlayScene extends Phaser.Scene {
  private player!: Player;
  private walls!: Phaser.Physics.Arcade.StaticGroup;
  private hazards!: Phaser.Physics.Arcade.StaticGroup;
  // Directional spikes (the four-direction `Spike` element). Separate
  // from `hazards` because the kill / block decision depends on the
  // player's velocity — wired with a processCallback collider that
  // kills only when the player moves INTO the spike tips, otherwise
  // blocks like a regular wall. Each rect carries its `dir` via
  // setData('dir', ...).
  private directionalSpikes!: Phaser.Physics.Arcade.StaticGroup;
  // Glass walls: act as walls until the player touches them, then break
  // after their per-instance `delay` (seconds). Separate group so we can
  // attach a per-collision callback that's not run for ordinary walls.
  private glassWalls!: Phaser.Physics.Arcade.StaticGroup;
  // Spike blocks: full-cell elements that BLOCK the player AND kill on
  // contact. Separate group so a single collider+overlap pair can apply
  // both behaviors.
  private killableWalls!: Phaser.Physics.Arcade.StaticGroup;
  // Coins: PURE VISUALS (no physics body) — pickup is checked manually
  // each frame via AABB distance. We deliberately avoid the Phaser
  // physics path because `physics.add.overlap` still sets the player
  // body's `touching.left/right/up/down` flags as a side-effect even
  // though it doesn't separate; FLYING_H reads those flags to detect
  // walls, so a coin in the flight path would falsely trigger a rebound.
  private coins!: Phaser.GameObjects.Group;
  private coinCount = 0;
  // Half the player's body height + half the coin's display size; cached
  // so the pickup loop doesn't recompute it 60 times a second.
  private coinPickupThreshold = 0;
  // Cannons fire on a timer; PlayScene.update ticks each one. Bullets
  // are pooled in `bullets` and despawn on contact with anything.
  private cannons: Cannon[] = [];
  private bullets!: Phaser.GameObjects.Group;
  // Keys: pure visuals (same no-body pattern as coins so flying through
  // them doesn't trip the player's wall-detection). Picking up a key
  // removes ALL key_walls of the matching color on the page.
  private keys!: Phaser.GameObjects.Group;
  private keyPickupThreshold = 0;
  // Key walls: solid wall-like static bodies in their own group, so the
  // pickup-driven removal can iterate JUST the matching-color walls.
  private keyWalls!: Phaser.Physics.Arcade.StaticGroup;
  // Gears tick in PlayScene.update; collisions handled via overlap with
  // the player. Bullets pass through gears (matching Godot — gears are
  // collision_mask=2, player only).
  private gears: Gear[] = [];
  // Portals: paired teleporters. Same "bullets pass through, only player
  // triggers" pattern as gears. Tick ticks the cooldown timers.
  private portals: Portal[] = [];
  // Turrets: like cannons (wall-like, fire on a timer) but the barrel
  // tracks the player. Built before the player exists, so PlayScene
  // calls turret.setPlayer() once player is constructed.
  private turrets: Turret[] = [];
  // Laser cannons: wall-like, with a beam that raycasts each frame and
  // kills the player on segment-vs-AABB overlap. Like turrets, they're
  // added to the walls group so the player + bullets collide with the
  // base cell.
  private laserCannons: LaserCannon[] = [];
  // Spikes / spike-blocks with duration > 0: tracked for the extend /
  // retract cycle. Each entry holds the lethal rect (the only part
  // that toggles — for directional spikes the wall-plate is always-on
  // and lives separately in the walls group). Toggling flips the
  // visual + the static body's `enable` flag so collision and overlap
  // both stop while retracted.
  private timedSpikes: Array<{
    onToggle: TimedSpikeToggle;
    duration: number;
    downtime: number;
    firing: boolean;
    phaseTimer: number;
  }> = [];
  // Teleports + exit use the no-body distance-trigger pattern so the
  // player passes through them visually without their physics body
  // tripping FLYING_H wall-detection (same trick as coins/keys).
  private teleports: Trigger[] = [];
  private exit: Trigger | null = null;
  // Multi-page state. startPageIndex comes in via init() from
  // scene.restart so a teleport can hand off to the next page; the
  // level itself is parsed once per scene run and cached.
  private startPageIndex = DEFAULT_PAGE_INDEX;
  private currentPageIndex = DEFAULT_PAGE_INDEX;
  private shouldFadeIn = false;
  private loadedLevel!: LevelData;
  // Set when init() receives an in-memory level (the editor's "Play"
  // hand-off). Non-null means: skip preload's file fetch and use the
  // provided level; restarts (cross-page teleports) re-pass it through
  // scene.restart so it survives across page transitions.
  private providedLevel: LevelData | null = null;
  // True iff PlayScene was launched from EditScene — surfaces a "Back
  // to Editor" button so the user can hop back without a refresh.
  private launchedFromEditor = false;
  // Disk path of the level being played, when launched from the editor.
  // Passed back to EditScene on the "back" round-trip so its Save
  // button still knows which file to write to.
  private levelPath: string | null = null;
  // Editor's unsaved-changes flag at the moment of Play hand-off, so
  // the round trip back to EditScene can restore it. Without this,
  // playing then returning would silently mark the editor "clean" and
  // let the user navigate away losing their unsaved changes.
  private editorDirty = false;
  // Campaign mode: 0 = not in campaign, 1..N = currently playing the
  // Nth campaign level. Drives preload (loads `levels/level-NN.json`)
  // and the level-complete dialog (offers Next Level / Back to Menu).
  // Persists across cross-page transitions so the trigger fires on
  // the correct campaign step when the player exits.
  private campaignLevel = 0;
  // Set when the game is running embedded inside the LevelCraft web
  // platform (iframe). Replaces MenuScene/EditScene exit paths with a
  // postMessage protocol — see src/embed.ts. Persists across restarts
  // (death-replay, cross-page, completion-replay).
  private embedLevelId: string | null = null;
  // Publish-verify mode: full-level playthrough launched by the host
  // page's PublishToggle when the level needs a fresh clear before it
  // can be published. Differs from a normal `launchedFromEditor` test
  // play in two ways: it always starts at page 0 (the level's spawn),
  // and on reaching the exit it posts `level-cleared` to the host so
  // the playtest gate can be unlocked. Persists across cross-page
  // restarts the same way the other init flags do.
  private publishVerify = false;
  // Black overlay for transition fade-out / fade-in. Pinned to the
  // camera (scrollFactor 0) so it covers the whole viewport regardless
  // of the centered-room camera scroll.
  private fadeOverlay!: Phaser.GameObjects.Rectangle;
  private transitioning = false;
  // Developer-facing state readout (player state / velocity / touching /
  // blocked). Only created when `?dev=1` is in the URL — hidden from
  // players (campaign, itch standalone, the embedded web player). When
  // undefined the per-frame update is skipped.
  private debugText?: Phaser.GameObjects.Text;
  private hudText!: Phaser.GameObjects.Text;
  // DOM "Back to Editor" button — only shown when launched from EditScene.
  private backButton: HTMLDivElement | null = null;

  constructor() {
    super('PlayScene');
  }

  // Receives data from scene.restart on cross-page transitions and from
  // EditScene's "Play" hand-off. First boot has no data, so falls back to
  // DEFAULT_PAGE_INDEX with no fade-in (instant initial render).
  init(data?: {
    pageIndex?: number;
    fadeIn?: boolean;
    level?: LevelData;
    fromEditor?: boolean;
    levelPath?: string;
    editorDirty?: boolean;
    campaignLevel?: number;
    embedLevelId?: string;
    publishVerify?: boolean;
  }): void {
    this.startPageIndex = data?.pageIndex ?? DEFAULT_PAGE_INDEX;
    this.shouldFadeIn = data?.fadeIn ?? false;
    this.providedLevel = data?.level ?? null;
    // Sticky: once launched from the editor, in-game page transitions
    // preserve the flag (restart-through-transitionToPage re-passes it).
    this.launchedFromEditor = data?.fromEditor ?? false;
    this.levelPath = data?.levelPath ?? null;
    this.editorDirty = data?.editorDirty ?? false;
    this.campaignLevel = data?.campaignLevel ?? 0;
    this.embedLevelId = data?.embedLevelId ?? null;
    this.publishVerify = data?.publishVerify ?? false;
  }

  preload(): void {
    // Skip the file fetch when an in-memory level was handed to us —
    // the editor's level may be unsaved and not yet on disk.
    if (!this.providedLevel) {
      // Same Phaser-cache-vs-key issue as EditScene.preload: the
      // JSON cache is keyed by LEVEL_KEY, so a prior scene run
      // could have left a different level's data under this key.
      // Clear it so this load fetches the actual current URL.
      if (this.cache.json.has(LEVEL_KEY)) {
        this.cache.json.remove(LEVEL_KEY);
      }
      // Campaign mode → the corresponding numbered level file.
      // Otherwise fall back to the legacy DEFAULT_LEVEL_URL sandbox.
      const url = this.campaignLevel > 0
        ? `levels/level-${String(this.campaignLevel).padStart(2, '0')}.json`
        : DEFAULT_LEVEL_URL;
      this.load.json(LEVEL_KEY, url);
    }

    // All visual sprites — shared with EditScene via the helper. Loaded
    // native (144×144 for most, 180×180 for cannon, 1448×1086 for the
    // background); each render call sets displaySize so visuals match
    // the 48px world grid.
    loadSprites(this);
    loadSounds(this);
  }

  create(): void {
    this.cameras.main.setBackgroundColor(COLOR_BACKGROUND);

    // Background music. Lives on the global sound manager so it survives
    // scene.restart (cross-page teleport, death respawn) without
    // re-starting from the beginning. Other scenes (Menu, Edit) stop it
    // explicitly in their own create — playback is scoped to gameplay.
    const bgm = this.sound.get(SOUND_KEYS.bgm)
      ?? this.sound.add(SOUND_KEYS.bgm, { loop: true, volume: SOUND_VOLUMES.bgm });
    if (!bgm.isPlaying) {
      bgm.play();
    }

    const raw = (this.providedLevel ?? this.cache.json.get(LEVEL_KEY)) as unknown;
    this.loadedLevel = validateLevel(raw, DEFAULT_LEVEL_URL);
    this.currentPageIndex = this.startPageIndex;
    const page = this.loadedLevel.pages[this.currentPageIndex];
    if (!page) {
      throw new Error(`Level has no page index ${this.currentPageIndex}`);
    }
    this.transitioning = false;

    const cols = page.tiles[0]!.length;
    const rows = page.tiles.length;

    // Center the room within the 1600x960 design viewport. Camera
    // scrollX = -offsetX makes world (0,0) appear at screen (offsetX, 0).
    const offsetX = (this.scale.gameSize.width - cols * TILE_SIZE) / 2;
    const offsetY = (this.scale.gameSize.height - rows * TILE_SIZE) / 2;
    this.cameras.main.setScroll(-offsetX, -offsetY);

    this.ensureAnimations();
    this.drawBackground();
    this.drawGridBackground(cols, rows);

    this.walls = this.physics.add.staticGroup();
    this.hazards = this.physics.add.staticGroup();
    this.directionalSpikes = this.physics.add.staticGroup();
    this.glassWalls = this.physics.add.staticGroup();
    this.killableWalls = this.physics.add.staticGroup();
    this.coins = this.add.group();  // plain group — no physics, see field comment
    this.coinCount = 0;
    this.bullets = this.add.group();
    this.cannons = [];
    this.keys = this.add.group();
    this.keyWalls = this.physics.add.staticGroup();
    this.gears = [];
    this.portals = [];
    this.turrets = [];
    this.laserCannons = [];
    this.timedSpikes = [];
    this.teleports = [];
    this.exit = null;
    this.buildWalls(page);
    this.buildSpikes(page);
    this.buildGlassWalls(page);
    this.buildSpikeBlocks(page);
    this.buildConveyors(page);
    this.buildCannons(page);
    this.buildKeyWalls(page);
    this.buildKeys(page);
    this.buildGears(page);
    this.buildPortals(page);
    this.buildTurrets(page);
    this.buildLaserCannons(page);
    this.buildTextLabels(page);
    this.buildTeleports(page);
    this.buildExit();

    // Input wiring — the player gets references to the cursor keys and
    // jump key so it doesn't have to reach into the scene's input plugin.
    const keyboard = this.input.keyboard;
    if (!keyboard) {
      throw new Error('PlayScene requires the keyboard plugin');
    }
    const cursors = keyboard.createCursorKeys();
    const jumpKey = keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.SPACE);

    const spawnX = (page.spawn.x + 0.5) * TILE_SIZE;
    const spawnY = (page.spawn.y + 0.5) * TILE_SIZE;
    this.player = new Player(this, spawnX, spawnY, cursors, jumpKey);
    // On death-respawn, restore the level's reset-on-death state
    // (glass walls, keys, and the key walls keys had unlocked).
    // Coins, gear progress, timed-spike phase, etc. are not reset —
    // only the things the player consumed/broke this life.
    this.player.on('respawn', () => this.resetLevelOnRespawn());
    // Wall-hit camera shake + sound. Player emits cells flown, axis, and
    // surface (wall / ceiling / floor) at every wall-terminating motion;
    // the magnitude formula and threshold live in feel.ts so the feel
    // can be tuned without touching scene code.
    this.player.on('wall-hit', (info: WallHitInfo) => {
      this.shakeOnWallHit(info);
      this.playWallHitSound(info);
    });
    // Turrets are built before the player; hand them the player ref now
    // so trackPlayer() can read its position each frame.
    for (const turret of this.turrets) {
      turret.setPlayer(this.player);
    }

    this.physics.add.collider(this.player, this.walls);
    this.physics.add.overlap(this.player, this.hazards, () => this.player.die());
    // Directional spikes: a velocity-aware collider. The processCallback
    // runs BEFORE separation; we kill when the player's velocity points
    // INTO the spike tips (matching `dir`), and otherwise return true to
    // let the collider block the player like a wall. Returning false in
    // the lethal branch skips separation so the death-pop animation can
    // carry the body cleanly past the rect.
    this.physics.add.collider(
      this.player,
      this.directionalSpikes,
      undefined,
      (_p, spike) => this.processDirectionalSpike(spike as Phaser.GameObjects.Image),
    );
    // Glass walls: collide normally; the callback starts the break timer
    // on first contact and ignores subsequent contacts until the wall
    // self-destructs.
    this.physics.add.collider(this.player, this.glassWalls, (_player, glass) => {
      this.triggerGlassWall(glass as Phaser.GameObjects.GameObject);
    });
    // Spike blocks: collide (player physically stops) AND overlap (player
    // dies). Both fire on contact; the death animation's collision-off
    // takes care of the body separating cleanly afterward.
    this.physics.add.collider(this.player, this.killableWalls);
    this.physics.add.overlap(this.player, this.killableWalls, () => this.player.die());
    // (Coin pickup is handled manually in update() via distance check —
    // see the field comment on `coins`.) Pre-compute the AABB threshold:
    // half player width (TILE_SIZE/2) plus half coin width (0.825 * TILE_SIZE / 2).
    this.coinPickupThreshold = TILE_SIZE * 0.5 + TILE_SIZE * 0.4125;
    // Half player width + half key display size (key now renders at
    // 1.1875 tile to match the visible art, so the pickup reach grows
    // accordingly — picking up a key visually overlapping the player
    // shouldn't require the player to be dead-center on it).
    this.keyPickupThreshold = TILE_SIZE * 0.5 + TILE_SIZE * 0.59375;
    // Key walls are walls — block the player. Bullet despawn against
    // them is added below alongside the other wall-like groups.
    this.physics.add.collider(this.player, this.keyWalls);

    // Bullets despawn on contact with anything wall-like. The same callback
    // handles all wall-like groups; the per-bullet `ignoreBody`/`ignoreTimer`
    // pattern lets future shooters (turrets) exempt themselves at oblique
    // firing angles. Cannons fire perpendicular so they never set ignoreBody.
    const handleBulletWallHit: Phaser.Types.Physics.Arcade.ArcadePhysicsCallback = (bullet, wall) => {
      const b = bullet as unknown as Bullet;
      if (!b.active) return;  // already destroyed (multi-overlap same frame)
      if (b.ignoreBody === (wall as unknown as Phaser.GameObjects.GameObject) && b.ignoreTimer > 0) return;
      b.destroy();
    };
    this.physics.add.overlap(this.bullets, this.walls, handleBulletWallHit);
    this.physics.add.overlap(this.bullets, this.glassWalls, handleBulletWallHit);
    this.physics.add.overlap(this.bullets, this.killableWalls, handleBulletWallHit);
    this.physics.add.overlap(this.bullets, this.hazards, handleBulletWallHit);
    this.physics.add.overlap(this.bullets, this.keyWalls, handleBulletWallHit);
    this.physics.add.overlap(this.bullets, this.directionalSpikes, handleBulletWallHit);
    // Bullet vs player: kill + despawn.
    this.physics.add.overlap(this.player, this.bullets, (_player, bullet) => {
      this.player.die();
      bullet.destroy();
    });
    // Player vs gears (overlap kills). Gears are intentionally NOT in
    // the bullet wall-hit handler — bullets pass through them, mirroring
    // the Godot collision_mask=2 (player only) configuration.
    this.physics.add.overlap(this.player, this.gears, () => this.player.die());
    // Portals: same "player only" pattern. The portal's own callback
    // does the teleport + cooldown.
    this.physics.add.overlap(this.player, this.portals, (_player, portal) => {
      (portal as Portal).handlePlayerOverlap(this.player);
    });

    // HUD — anchored to the screen, not the world, so it doesn't move
    // when the camera scrolls (setScrollFactor(0) is the standard idiom).
    this.add
      .text(
        16,
        this.scale.gameSize.height - 28,
        `${this.loadedLevel.name}  (page ${this.currentPageIndex + 1}/${this.loadedLevel.pages.length})  |  Arrows: launch  |  Space: jump`,
        { color: '#9aa0a8', fontSize: '14px' },
      )
      .setScrollFactor(0);

    // Coin counter — top-left, the player-facing HUD slot.
    this.hudText = this.add
      .text(16, 16, '', {
        color: '#ffd933',
        fontSize: '20px',
        fontFamily: 'monospace',
      })
      .setScrollFactor(0);

    // Debug state (developer-facing) — top-right, opt-in via `?dev=1` so
    // it never crowds a real player's screen. Mirrors MenuScene's dev
    // toggle. When the param is absent `this.debugText` stays undefined
    // and update() skips the readout entirely.
    if (new URLSearchParams(window.location.search).get('dev') === '1') {
      this.debugText = this.add
        .text(this.scale.gameSize.width - 16, 16, '', {
          color: '#cccccc',
          fontSize: '14px',
          fontFamily: 'monospace',
          align: 'right',
        })
        .setOrigin(1, 0)
        .setScrollFactor(0);
    }

    // Fade overlay (full design viewport, scroll-locked, on top of
    // everything). Starts opaque on a transition entrance so the player
    // sees a clean fade-in; instant-render on the very first scene boot.
    this.fadeOverlay = this.add
      .rectangle(0, 0, this.scale.gameSize.width, this.scale.gameSize.height, 0x000000)
      .setOrigin(0, 0)
      .setScrollFactor(0)
      .setDepth(1000)
      .setAlpha(this.shouldFadeIn ? 1 : 0);
    if (this.shouldFadeIn) {
      this.tweens.add({
        targets: this.fadeOverlay,
        alpha: 0,
        duration: FADE_DURATION_MS,
      });
    }

    // Always show a back button during play. Editor-launched runs go
    // straight back to the editor (fast iterate); campaign / standalone
    // runs prompt with a Continue / Exit dialog so the player doesn't
    // lose progress to a stray click. Plain embedded runs hide the
    // in-game back button — the host page owns navigation. The
    // exception is an editor-launched test-play inside the iframe:
    // there we DO want the in-iframe '◀ Edit' button so the user can
    // return to the editor without leaving the page (the host's own
    // back link would just navigate the iframe URL away from /edit).
    if (!this.embedLevelId || this.launchedFromEditor) {
      this.buildBackButton();
    }
    // Tear down DOM additions on shutdown so they don't linger after a
    // scene switch (back to editor / menu) or hot reload.
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.destroyBackButton());
    this.events.once(Phaser.Scenes.Events.DESTROY, () => this.destroyBackButton());

    // Embedded mode: tell the host this play session has begun. Fires
    // on initial boot and on post-clear replay, but NOT on cross-page
    // transitions (those carry shouldFadeIn=true and aren't a fresh
    // attempt — within-session navigation only). Also skipped when the
    // play session was launched from the editor (the author test-
    // playing their own draft shouldn't inflate play_count).
    if (this.embedLevelId && !this.shouldFadeIn && !this.launchedFromEditor) {
      postToParent('play-started', { levelId: this.embedLevelId });
    }
  }

  private buildBackButton(): void {
    if (this.backButton) return;
    const label = this.launchedFromEditor ? '◀ Edit' : '◀ Exit';
    const wrap = document.createElement('div');
    wrap.className = 'editor-overlay';
    wrap.innerHTML = `
      <div class="editor-back-wrap">
        <button class="editor-btn">${label}</button>
      </div>
    `;
    document.body.appendChild(wrap);
    this.backButton = wrap;
    wrap.querySelector('button')?.addEventListener('click', () => {
      this.sound.play(SOUND_KEYS.uiButton, { volume: SOUND_VOLUMES.sfx });
      if (this.launchedFromEditor) {
        // Direct back — keeps the test → tweak → test loop fast.
        this.scene.start('EditScene', {
          level: this.providedLevel ?? this.loadedLevel,
          pageIndex: this.currentPageIndex,
          levelPath: this.levelPath ?? undefined,
          dirty: this.editorDirty,
          embedLevelId: this.embedLevelId ?? undefined,
        });
      } else {
        this.showExitConfirm();
      }
    });
  }

  private destroyBackButton(): void {
    if (this.backButton) {
      this.backButton.remove();
      this.backButton = null;
    }
  }

  // Confirm dialog for non-editor exits. Pauses the scene while the
  // modal is open so the player doesn't keep falling / dying behind
  // the dialog. Continue resumes, Exit returns to MenuScene.
  private showExitConfirm(): void {
    // Guard against double-click spawning two dialogs.
    if (document.querySelector('.editor-modal[data-modal-role="exit-confirm"]')) {
      return;
    }
    this.scene.pause();

    const dialog = document.createElement('div');
    dialog.className = 'editor-modal';
    dialog.setAttribute('data-modal-role', 'exit-confirm');
    dialog.innerHTML = `
      <div class="editor-modal-backdrop"></div>
      <div class="editor-modal-content">
        <p>Exit to menu?</p>
        <div class="editor-modal-actions">
          <button data-modal-action="exit" class="editor-modal-btn editor-modal-btn-danger">Exit</button>
          <button data-modal-action="continue" class="editor-modal-btn editor-modal-btn-primary">Continue</button>
        </div>
      </div>
    `;
    document.body.appendChild(dialog);
    dialog.addEventListener('click', (ev) => {
      const t = ev.target as HTMLElement;
      const action = t.getAttribute('data-modal-action');
      if (!action) return;
      this.sound.play(SOUND_KEYS.uiButton, { volume: SOUND_VOLUMES.sfx });
      if (action === 'continue') {
        dialog.remove();
        this.scene.resume();
      } else if (action === 'exit') {
        dialog.remove();
        // scene.start handles cleanup of the paused PlayScene.
        this.scene.start('MenuScene');
      }
    });
  }

  update(time: number, delta: number): void {
    this.player.update(time, delta);
    this.checkCoinPickups();
    this.checkKeyPickups();
    this.checkPageTriggers();

    const dt = delta / 1000;
    for (const cannon of this.cannons) {
      cannon.tick(dt);
    }
    for (const gear of this.gears) {
      gear.tick(dt);
    }
    for (const portal of this.portals) {
      portal.tick(dt);
    }
    for (const turret of this.turrets) {
      turret.tick(dt);
    }
    for (const laser of this.laserCannons) {
      laser.tick(dt);
      // Beam-vs-player kill: arcade physics has no rotated-body support,
      // so the laser does its own segment-vs-AABB test. One break is
      // enough — die() is idempotent on already-dead state.
      if (laser.checkPlayerHit(this.player)) {
        this.player.die();
      }
    }
    // Timed spikes: extend / retract cycle. Only the lethal rect
    // toggles. For spike_blocks, the small central plate stays in
    // `walls` permanently — it provides the "small cube blocks the
    // player when retracted" behavior without any per-frame body
    // bookkeeping. While the spike is extended, the player dies on
    // contact and `die()` clears checkCollision, so the dying body
    // passes the plate harmlessly.
    //
    // phaseTimer === Infinity is used as the "no further toggles"
    // sentinel — happens when a spike with duration === 0 is in its
    // permanently-extended phase after a one-shot delay.
    for (const ts of this.timedSpikes) {
      if (!Number.isFinite(ts.phaseTimer)) continue;
      ts.phaseTimer -= dt;
      if (ts.phaseTimer <= 0) {
        ts.firing = !ts.firing;
        if (ts.firing) {
          // Entering the extended phase — duration 0 means "stay
          // extended forever" (reachable only via the delay path).
          ts.phaseTimer = ts.duration > 0 ? ts.duration : Number.POSITIVE_INFINITY;
        } else {
          ts.phaseTimer = ts.downtime;
        }
        ts.onToggle(ts.firing);
      }
    }
    // Tick all live bullets (decrements ignoreTimer + lifetime).
    this.bullets.getChildren().forEach((b) => {
      const bullet = b as Bullet;
      if (bullet.active) bullet.tick(dt);
    });

    if (this.debugText) {
      const t = this.player.body.touching;
      const b = this.player.body.blocked;
      this.debugText.setText([
        `state: ${PlayerState[this.player.state]}`,
        `vel: (${this.player.body.velocity.x.toFixed(0)}, ${this.player.body.velocity.y.toFixed(0)})`,
        `touching: ${t.up ? 'U' : '-'}${t.down ? 'D' : '-'}${t.left ? 'L' : '-'}${t.right ? 'R' : '-'}`,
        `blocked:  ${b.up ? 'U' : '-'}${b.down ? 'D' : '-'}${b.left ? 'L' : '-'}${b.right ? 'R' : '-'}`,
      ]);
    }
    this.hudText.setText(`coins: ${this.coinCount}`);
  }

  private buildWalls(page: PageData): void {
    // Tile-grid pass: 'W' = wall, 'C' = coin, '.' = empty. Other chars are
    // ignored (forward-compat for future tile-char additions).
    for (let r = 0; r < page.tiles.length; r++) {
      const row = page.tiles[r]!;
      for (let c = 0; c < row.length; c++) {
        const ch = row.charAt(c);
        if (ch === 'W') {
          this.makeWall(c, r);
        } else if (ch === 'C') {
          this.makeCoin(c, r);
        }
      }
    }
  }

  private makeCoin(col: number, row: number): void {
    const x = (col + 0.5) * TILE_SIZE;
    const y = (row + 0.5) * TILE_SIZE;
    // Sized smaller than a tile so the coin reads as a pickup, not a
    // tile. No physics body — pickup is handled by checkCoinPickups().
    const size = TILE_SIZE * 0.825;
    const visual = this.add.sprite(x, y, 'coin_0');
    visual.setDisplaySize(size, size);
    visual.play('coin_spin');
    this.coins.add(visual);
  }

  // AABB-style pickup check. The player's body width is roughly TILE_SIZE
  // (the per-state shape narrows by 2-4px on the perpendicular axis;
  // ignoring those few pixels here doesn't matter for pickup feel).
  private checkCoinPickups(): void {
    const px = this.player.x;
    const py = this.player.y;
    const t = this.coinPickupThreshold;
    const children = this.coins.getChildren();
    // Iterate backwards so destroying mid-loop doesn't skip elements.
    for (let i = children.length - 1; i >= 0; i--) {
      const coin = children[i] as Phaser.GameObjects.Sprite;
      if (Math.abs(coin.x - px) < t && Math.abs(coin.y - py) < t) {
        coin.destroy();
        this.coinCount += 1;
        this.sound.play(SOUND_KEYS.getCoin, { volume: SOUND_VOLUMES.sfx });
      }
    }
  }

  private makeWall(col: number, row: number): void {
    const x = (col + 0.5) * TILE_SIZE;
    const y = (row + 0.5) * TILE_SIZE;
    // Native PNG is 144×144; scale display + body to TILE_SIZE so the
    // visual matches the 48px world grid. updateFromGameObject syncs
    // the static body to the new display size.
    const wall = this.walls.create(x, y, 'wall') as Phaser.Physics.Arcade.Image;
    wall.setDisplaySize(TILE_SIZE, TILE_SIZE);
    const body = wall.body as Phaser.Physics.Arcade.StaticBody;
    body.setSize(TILE_SIZE, TILE_SIZE);
    body.updateFromGameObject();
  }

  private buildSpikes(page: PageData): void {
    if (!page.spikes) {
      return;
    }
    for (const spike of page.spikes) {
      const teeth = this.makeSpike(spike.x, spike.y, spike.dir);
      this.maybeRegisterTimedSpike(
        (firing) => {
          teeth.setVisible(firing);
          (teeth.body as Phaser.Physics.Arcade.StaticBody).enable = firing;
        },
        spike.duration,
        spike.downtime,
        spike.delay,
      );
    }
  }

  private makeSpike(col: number, row: number, dir: CardinalDir): Phaser.GameObjects.Image {
    const layout = SPIKE_LAYOUT[dir];
    // Plate body: invisible half-cell static body in `walls` so the
    // non-lethal half still blocks the player. The full-cell spike_0
    // base sprite below covers the cell visually for both halves.
    this.makeInvisibleStaticBody(col, row, layout.plate, this.walls);
    // Base sprite — full cell, rotated so the "open" side faces the
    // lethal direction. Decorative; no body.
    const cx = (col + 0.5) * TILE_SIZE;
    const cy = (row + 0.5) * TILE_SIZE;
    this.add
      .image(cx, cy, 'spike_0')
      .setDisplaySize(TILE_SIZE, TILE_SIZE)
      .setRotation(SPIKE_ROTATION[dir]);
    // Teeth: spike_1 at native aspect, on the lethal edge. Carries the
    // half-cell static body for the velocity-aware kill check.
    const teeth = this.makeSpikeTeeth(col, row, dir);
    teeth.setData('dir', dir);
    return teeth;
  }

  // Invisible static body — used where the visible art is provided by
  // a separate (no-body) sprite, but we still need a static body for
  // collision (e.g. the wall half of a directional spike, the hazard
  // body of a spike block).
  private makeInvisibleStaticBody(
    col: number,
    row: number,
    rect: Rect,
    group: Phaser.Physics.Arcade.StaticGroup,
  ): Phaser.GameObjects.Rectangle {
    const w = rect.w * TILE_SIZE;
    const h = rect.h * TILE_SIZE;
    const x = col * TILE_SIZE + (rect.x + rect.w / 2) * TILE_SIZE;
    const y = row * TILE_SIZE + (rect.y + rect.h / 2) * TILE_SIZE;
    // alpha 0 → invisible; the rect still has display bounds so the
    // group's body setup works normally.
    const r = this.add.rectangle(x, y, w, h, 0x000000, 0);
    group.add(r);
    const body = r.body as Phaser.Physics.Arcade.StaticBody;
    body.setSize(w, h);
    body.updateFromGameObject();
    return r;
  }

  private makeSpikeTeeth(col: number, row: number, dir: CardinalDir): Phaser.GameObjects.Image {
    const layout = SPIKE_LAYOUT[dir];
    const lethalRect = layout.spike;
    // Native teeth are 137×36, sized to align with a 144 base. Scale
    // to match the displayed base (TILE_SIZE / 144). The teeth strip
    // sits at the lethal edge of the cell. Math note: a "frame edge"
    // alignment would put offset = halfTile - teethH/2, but both the
    // spike_0 base art and spike_1 teeth art have a few pixels of
    // transparent padding inside their PNG frames. Aligning frame-to-
    // frame leaves a visible gap between teeth tips and the base's
    // visible edge — so push the teeth deeper into the cell by
    // teethH / 2 so the bases overlap the visible base art.
    const scale = TILE_SIZE / 144;
    const teethW = 137 * scale;
    const teethH = 36 * scale;
    const halfTile = TILE_SIZE / 2;
    const offset = halfTile - teethH;
    const cellCx = (col + 0.5) * TILE_SIZE;
    const cellCy = (row + 0.5) * TILE_SIZE;
    let vx = cellCx;
    let vy = cellCy;
    switch (dir) {
      case 'up':    vy -= offset; break;
      case 'down':  vy += offset; break;
      case 'left':  vx -= offset; break;
      case 'right': vx += offset; break;
    }
    const visual = this.add.image(vx, vy, 'spike_1');
    visual.setDisplaySize(teethW, teethH);
    visual.setRotation(SPIKE_ROTATION[dir]);
    this.directionalSpikes.add(visual);
    // Arcade physics has no rotated bodies, so set the static body to
    // the axis-aligned lethal half-cell directly. The visual's rotation
    // doesn't affect this — the kill check uses player velocity + dir.
    const bodyW = lethalRect.w * TILE_SIZE;
    const bodyH = lethalRect.h * TILE_SIZE;
    const bodyX = col * TILE_SIZE + lethalRect.x * TILE_SIZE;
    const bodyY = row * TILE_SIZE + lethalRect.y * TILE_SIZE;
    const body = visual.body as Phaser.Physics.Arcade.StaticBody;
    body.setSize(bodyW, bodyH);
    body.position.set(bodyX, bodyY);
    return visual;
  }

  // Registers a toggle callback when the spike has any timing behavior.
  // Skipped only when duration === 0 AND delay === 0 — that's the
  // legacy "always extended" case with no per-frame work to do.
  // With delay > 0 we register even when duration === 0, so a static
  // hazard can be staggered into existence after a delay.
  private maybeRegisterTimedSpike(
    onToggle: TimedSpikeToggle,
    duration: number | undefined,
    downtime: number | undefined,
    delay: number | undefined,
  ): void {
    const dur = duration ?? 0;
    const dly = delay ?? 0;
    if (dur <= 0 && dly <= 0) return;
    const down = downtime ?? 3.0;
    if (dly > 0) {
      // Spikes are constructed in the extended state (teeth visible,
      // hazard body enabled). Retract immediately so the level matches
      // what the delay specifies — the first frame the spike must look
      // and act retracted.
      onToggle(false);
      this.timedSpikes.push({
        onToggle,
        duration: dur,
        downtime: down,
        firing: false,
        phaseTimer: dly,
      });
    } else {
      this.timedSpikes.push({
        onToggle,
        duration: dur,
        downtime: down,
        firing: true,
        phaseTimer: dur,
      });
    }
  }

  // ProcessCallback for the player vs directional-spikes collider.
  // Runs BEFORE separation; the return value tells Phaser whether to
  // separate (`true` = block the player, `false` = ignore this pair
  // for this frame). We kill only when the player is moving DIRECTLY
  // INTO the spike tips — i.e., the velocity component on the lethal
  // axis both points toward the tips AND dominates the perpendicular
  // axis. This avoids false kills like "falling sideways past an
  // up-spike with a small gravity-induced v.y": |v.x| ≫ |v.y| means
  // the player isn't really moving INTO the tips, so we block instead
  // of killing. die() is idempotent so repeated triggers are harmless.
  private processDirectionalSpike(spike: Phaser.GameObjects.Image): boolean {
    const dir = spike.getData('dir') as CardinalDir;
    const v = this.player.body.velocity;
    const ax = Math.abs(v.x);
    const ay = Math.abs(v.y);
    const lethal =
      (dir === 'up' && v.y > 0 && ay > ax) ||
      (dir === 'down' && v.y < 0 && ay > ax) ||
      (dir === 'left' && v.x > 0 && ax > ay) ||
      (dir === 'right' && v.x < 0 && ax > ay);
    if (lethal) {
      this.player.die();
      return false;  // skip separation; death-pop carries the body away
    }
    return true;     // separate (block like a wall)
  }

  // Death-respawn hook: rebuilds the page elements that the player
  // consumed/broke this life so the next attempt starts from a clean
  // slate. `clear(true, true)` removes from group, removes from scene,
  // and destroys each child — Phaser's tween manager auto-drops any
  // pending tween (e.g. mid-fade glass walls) targeting destroyed
  // Camera shake driven by the player's wall-hit event. cells < min
  // means a light bump and is silent. Above min, magnitude grows
  // linearly per axis (vertical drops scale faster than horizontal
  // flights, see feel.ts). Intensity is locked to the impact axis via
  // a Vector2 so a hard floor landing only shakes vertically. force=true
  // re-arms the shake if a previous one is still running — back-to-back
  // wall hits should both register.
  private shakeOnWallHit(info: WallHitInfo): void {
    if (info.cells < SHAKE_MIN_CELLS) return;
    const slope = info.axis === 'horizontal' ? SHAKE_SLOPE_PER_CELL_H : SHAKE_SLOPE_PER_CELL_V;
    const magnitude = 1.0 + (info.cells - SHAKE_MIN_CELLS) * slope;
    const intensity = SHAKE_BASE_INTENSITY * magnitude;
    const vec = info.axis === 'horizontal'
      ? new Phaser.Math.Vector2(intensity, 0)
      : new Phaser.Math.Vector2(0, intensity);
    this.cameras.main.shake(SHAKE_DURATION_MS, vec, true);
  }

  // Pick the right SFX for a wall hit: a downward-terminating motion
  // is a landing (LandGround), anything else (side wall, ceiling bump)
  // is a generic impact (HitWall). Plays unconditionally — the visual
  // shake has its own min-cells threshold, but a tiny "tap" still
  // sounds about right for arcade feel.
  private playWallHitSound(info: WallHitInfo): void {
    const key = info.surface === 'floor' ? SOUND_KEYS.landGround : SOUND_KEYS.hitWall;
    this.sound.play(key, { volume: SOUND_VOLUMES.sfx });
  }

  // objects. We then re-run the same build helpers used in create(),
  // pulling fresh state from the level data.
  private resetLevelOnRespawn(): void {
    const page = this.loadedLevel.pages[this.currentPageIndex];
    if (!page) return;
    this.glassWalls.clear(true, true);
    this.keys.clear(true, true);
    this.keyWalls.clear(true, true);
    this.buildGlassWalls(page);
    this.buildKeyWalls(page);
    this.buildKeys(page);
  }

  private buildGlassWalls(page: PageData): void {
    if (!page.glass_walls) {
      return;
    }
    for (const gw of page.glass_walls) {
      this.makeGlassWall(gw.x, gw.y, gw.delay);
    }
  }

  private makeGlassWall(col: number, row: number, delay: number): void {
    const x = (col + 0.5) * TILE_SIZE;
    const y = (row + 0.5) * TILE_SIZE;
    const visual = this.add.image(x, y, 'glass-wall');
    visual.setDisplaySize(TILE_SIZE, TILE_SIZE);
    this.glassWalls.add(visual);
    visual.setData('delay', delay);
    visual.setData('triggered', false);
    const body = visual.body as Phaser.Physics.Arcade.StaticBody;
    body.setSize(TILE_SIZE, TILE_SIZE);
    body.updateFromGameObject();
  }

  // First player contact starts the break timer for the WHOLE connected
  // shatter group: 4-adjacent glass walls flood-filled from the touched
  // cell. Reads physically as "this row/pane of glass is one barrier and
  // it's all cracked" instead of "pop, pop, pop" as the player traverses
  // each cell. Already-triggered members are skipped so neighbouring
  // groups touched in the same frame don't double-schedule.
  private triggerGlassWall(glass: Phaser.GameObjects.GameObject): void {
    if (glass.getData('triggered')) {
      return;
    }
    this.sound.play(SOUND_KEYS.hitGlass, { volume: SOUND_VOLUMES.sfx });
    const group = this.findGlassGroup(glass as Phaser.GameObjects.Image);
    for (const member of group) {
      if (member.getData('triggered')) continue;
      member.setData('triggered', true);
      const delaySec = (member.getData('delay') as number) ?? 1.5;
      // Fade alpha 1 → 0 over half the per-instance delay (visibly
      // ~2× faster than the configured break time), then destroy.
      // Body collision is disabled once the fade passes the halfway
      // point (alpha < 0.5) so the player isn't blocked by a near-
      // invisible wall — the first touch still rebounds (body is live
      // at full alpha), but mid-fade the glass becomes pass-through.
      this.tweens.add({
        targets: member,
        alpha: 0,
        duration: delaySec * 500,
        onUpdate: () => {
          const body = member.body as Phaser.Physics.Arcade.StaticBody | null;
          if (body && body.enable && member.alpha < 0.5) {
            body.enable = false;
          }
        },
        onComplete: () => member.destroy(),
      });
    }
  }

  // BFS over 4-adjacent glass-wall cells starting from `start`. Cell key
  // is `col,row` derived from each image's center position (each wall is
  // 1×1 tile, axis-aligned, so floor(x/TILE) gives the column).
  private findGlassGroup(
    start: Phaser.GameObjects.Image,
  ): Phaser.GameObjects.Image[] {
    const cellMap = new Map<string, Phaser.GameObjects.Image>();
    for (const obj of this.glassWalls.getChildren()) {
      const gw = obj as Phaser.GameObjects.Image;
      const col = Math.floor(gw.x / TILE_SIZE);
      const row = Math.floor(gw.y / TILE_SIZE);
      cellMap.set(`${col},${row}`, gw);
    }
    const result: Phaser.GameObjects.Image[] = [];
    const visited = new Set<string>();
    const queue: { col: number; row: number }[] = [
      { col: Math.floor(start.x / TILE_SIZE), row: Math.floor(start.y / TILE_SIZE) },
    ];
    while (queue.length > 0) {
      const { col, row } = queue.shift()!;
      const key = `${col},${row}`;
      if (visited.has(key)) continue;
      visited.add(key);
      const gw = cellMap.get(key);
      if (!gw) continue;
      result.push(gw);
      queue.push({ col: col + 1, row });
      queue.push({ col: col - 1, row });
      queue.push({ col, row: row + 1 });
      queue.push({ col, row: row - 1 });
    }
    return result;
  }

  private buildSpikeBlocks(page: PageData): void {
    if (!page.spike_blocks) {
      return;
    }
    for (const sb of page.spike_blocks) {
      const built = this.makeSpikeBlock(sb.x, sb.y);
      this.maybeRegisterTimedSpike(
        (firing) => {
          for (const t of built.teeth) t.setVisible(firing);
          (built.hazard.body as Phaser.Physics.Arcade.StaticBody).enable = firing;
        },
        sb.duration,
        sb.downtime,
        sb.delay,
      );
    }
  }

  // Spike block visual / collision layout:
  //   base   — spike_2 image, full cell (decorative, no body).
  //   teeth  — 4 × spike_3 images, one per side, tips pointing outward.
  //            Toggled visible/hidden by the timed-spike system.
  //   hazard — invisible full-cell static body in `hazards` (overlap
  //            kills the player). Toggled enable in lockstep with teeth
  //            so retracted = harmless.
  //   plate  — invisible small central cube in `walls` (collider, no
  //            kill). ALWAYS active. While the hazard is extended, the
  //            player dies before reaching the plate; while retracted,
  //            the plate is the only obstacle in the cell — see the
  //            tunneling notes below.
  // Plate width is intentionally 2/3 tile rather than 1/3. Phaser's
  // ProcessX.Set (node_modules/phaser/src/physics/arcade/) picks
  // separation direction by shortest-distance, NOT by velocity. When
  // a fast-moving player overshoots more than halfway through a small
  // static body in one frame, the shorter exit is forward — Phaser
  // pushes the player out the far side, tunneling them through the
  // cube. Math: to keep `body1OnLeft = false` for a player width 48
  // moving 32 px/frame, plate width must be > 16. 2/3 tile = 32 px.
  private makeSpikeBlock(col: number, row: number): {
    hazard: Phaser.GameObjects.Rectangle;
    teeth: Phaser.GameObjects.Image[];
  } {
    const cx = (col + 0.5) * TILE_SIZE;
    const cy = (row + 0.5) * TILE_SIZE;

    // Base sprite — full cell, decorative.
    this.add.image(cx, cy, 'spike_2').setDisplaySize(TILE_SIZE, TILE_SIZE);

    // Hazard body (invisible, full cell, in hazards group).
    const hazard = this.makeInvisibleStaticBody(
      col, row,
      { x: 0, y: 0, w: 1, h: 1 },
      this.hazards,
    );

    // Plate body (invisible, central cube, in walls — always-on).
    const plateRect: Rect = {
      x: 1 / 6, y: 1 / 6, w: 2 / 3, h: 2 / 3,
    };
    this.makeInvisibleStaticBody(col, row, plateRect, this.walls);

    // Teeth — 4 × spike_3, one per side. Native 83×30 scaled by
    // TILE_SIZE/144 so they align to the spike_2 base's expected
    // tooth slots. Same frame-padding compensation as the directional
    // spike: push teeth deeper into the cell by teethH so the bases
    // overlap the visible base art rather than the PNG frame edge.
    const scale = TILE_SIZE / 144;
    const teethW = 83 * scale;
    const teethH = 30 * scale;
    const halfTile = TILE_SIZE / 2;
    const offset = halfTile - teethH;
    const sides: Array<{ dx: number; dy: number; rot: number }> = [
      { dx: 0, dy: -offset, rot: 0 },              // top, tips up
      { dx: offset, dy: 0, rot: Math.PI / 2 },     // right, tips right
      { dx: 0, dy: offset, rot: Math.PI },         // bottom, tips down
      { dx: -offset, dy: 0, rot: -Math.PI / 2 },   // left, tips left
    ];
    const teeth: Phaser.GameObjects.Image[] = sides.map((s) =>
      this.add
        .image(cx + s.dx, cy + s.dy, 'spike_3')
        .setDisplaySize(teethW, teethH)
        .setRotation(s.rot),
    );

    return { hazard, teeth };
  }

  private buildConveyors(page: PageData): void {
    if (!page.conveyors) {
      return;
    }
    // Adjacency map keyed by `col,row` → dir. conveyorPieceFor (shared
    // with EditScene) reads it to decide left/middle/right per cell.
    const cellMap = new Map<string, ConveyorDir>();
    for (const cv of page.conveyors) {
      cellMap.set(`${cv.x},${cv.y}`, cv.dir);
    }
    for (const cv of page.conveyors) {
      const piece = conveyorPieceFor(cellMap, cv.x, cv.y, cv.dir);
      this.makeConveyor(cv.x, cv.y, cv.dir, piece);
    }
  }

  // Conveyor: a wall-like static body (so the player can stand on it
  // and it blocks horizontal flight) with a `conveyorDir` data tag the
  // player's idle probe reads to apply horizontal push. Lives in the
  // walls group so the existing player↔walls collider handles it.
  // Visual is the appropriate (dir, piece) animated belt — direction
  // is encoded in the art (arrows on the belt), so no overlay arrow.
  private makeConveyor(
    col: number,
    row: number,
    dir: 'cw' | 'ccw',
    piece: 'left' | 'middle' | 'right',
  ): void {
    const x = (col + 0.5) * TILE_SIZE;
    const y = (row + 0.5) * TILE_SIZE;
    const numericDir = dir === 'cw' ? 1 : -1;
    const sprite = this.add.sprite(x, y, `conveyor_${dir}_${piece}_0`);
    sprite.setDisplaySize(TILE_SIZE, TILE_SIZE);
    sprite.play(`conveyor_${dir}_${piece}`);
    this.walls.add(sprite);
    sprite.setData(CONVEYOR_DIR_DATA_KEY, numericDir);
    // The conveyor art has the belt in the lower-middle of its 144
    // frame: ~3/8 transparent padding above, ~1/2 tile of belt, small
    // decorative bottom strip. Body covers just the belt strip so the
    // player stands on the belt's visible top edge instead of floating
    // at the cell-top. setSize(_, _, false) skips Phaser's auto-recenter
    // so the explicit position.set below is authoritative.
    const body = sprite.body as Phaser.Physics.Arcade.StaticBody;
    body.setSize(TILE_SIZE, TILE_SIZE / 2, false);
    body.position.set(col * TILE_SIZE, row * TILE_SIZE + TILE_SIZE * 3 / 8);
  }

  private buildCannons(page: PageData): void {
    if (!page.cannons) {
      return;
    }
    for (const c of page.cannons) {
      const cannon = new Cannon(
        this,
        c.x,
        c.y,
        c.dir,
        c.period,
        c.bullet_speed,
        this.bullets,
        c.delay ?? 0,
      );
      // Add to the walls group so the existing player↔walls collider
      // physically blocks the player against the cannon's cell.
      this.walls.add(cannon);
      this.cannons.push(cannon);
    }
  }

  private buildKeys(page: PageData): void {
    if (!page.keys) {
      return;
    }
    for (const k of page.keys) {
      this.makeKey(k.x, k.y, k.color);
    }
  }

  // No physics body — same pattern as coins (a body would set the
  // player's `touching.X` flags as a side-effect and trip FLYING_H's
  // wall-detection). Pickup is a manual distance check in update().
  private makeKey(col: number, row: number, colorIdx: number): void {
    const x = (col + 0.5) * TILE_SIZE;
    const y = (row + 0.5) * TILE_SIZE;
    // Display larger than a tile: the key art has noticeable transparent
    // padding inside its 144×144 frame, so the visible key reads at
    // roughly player-size when the container is ~1.2 tile. No physics
    // body — pickup is handled by checkKeyPickups() below.
    const visual = this.add.sprite(x, y, `key_${colorIdx}_0`);
    visual.setDisplaySize(TILE_SIZE * 1.1875, TILE_SIZE * 1.1875);
    visual.play(`key_spin_${colorIdx}`);
    visual.setData('color', colorIdx);
    this.keys.add(visual);
  }

  private buildKeyWalls(page: PageData): void {
    if (!page.key_walls) {
      return;
    }
    for (const kw of page.key_walls) {
      this.makeKeyWall(kw.x, kw.y, kw.color);
    }
  }

  // Solid wall — player can't pass; bullet hits despawn it. Same group
  // shape as `walls`/`glassWalls`, just kept separate so picking up a
  // matching-color key only iterates these and not every wall on the page.
  private makeKeyWall(col: number, row: number, colorIdx: number): void {
    const x = (col + 0.5) * TILE_SIZE;
    const y = (row + 0.5) * TILE_SIZE;
    const visual = this.add.image(x, y, `key_wall_${colorIdx}`);
    visual.setDisplaySize(TILE_SIZE, TILE_SIZE);
    visual.setData('color', colorIdx);
    this.keyWalls.add(visual);
    const body = visual.body as Phaser.Physics.Arcade.StaticBody;
    body.setSize(TILE_SIZE, TILE_SIZE);
    body.updateFromGameObject();
  }

  private checkKeyPickups(): void {
    const px = this.player.x;
    const py = this.player.y;
    const t = this.keyPickupThreshold;
    const children = this.keys.getChildren();
    for (let i = children.length - 1; i >= 0; i--) {
      const key = children[i] as Phaser.GameObjects.Sprite;
      if (Math.abs(key.x - px) < t && Math.abs(key.y - py) < t) {
        const colorIdx = key.getData('color') as number;
        key.destroy();
        this.removeKeyWallsByColor(colorIdx);
        this.sound.play(SOUND_KEYS.getKey, { volume: SOUND_VOLUMES.sfx });
      }
    }
  }

  // Cascade: collecting a key destroys ALL key_walls that share its color.
  private removeKeyWallsByColor(colorIdx: number): void {
    const walls = this.keyWalls.getChildren();
    // Iterate backwards because destroy() removes from the group's list.
    for (let i = walls.length - 1; i >= 0; i--) {
      const wall = walls[i] as Phaser.GameObjects.Image;
      if (wall.getData('color') === colorIdx) {
        wall.destroy();
      }
    }
  }

  private buildTurrets(page: PageData): void {
    if (!page.turrets) {
      return;
    }
    for (const t of page.turrets) {
      const turret = new Turret(
        this,
        t.x,
        t.y,
        t.period,
        t.bullet_speed,
        this.bullets,
        t.delay ?? 0,
      );
      // Add to walls group so the player + bullets collide with the
      // turret's cell exactly like with a cannon.
      this.walls.add(turret);
      this.turrets.push(turret);
    }
  }

  // Beam-blocker check shared by every laser cannon on this page.
  // Queries live state — glass walls broken mid-play / key walls
  // removed after pickup stop blocking once they're gone. Out-of-
  // bounds counts as blocking so the beam terminates at the level
  // edge instead of running for full max-range.
  private isLaserBlocker = (col: number, row: number): boolean => {
    const page = this.loadedLevel.pages[this.currentPageIndex];
    if (!page) return true;
    if (row < 0 || col < 0) return true;
    if (row >= page.tiles.length) return true;
    if (col >= page.tiles[0]!.length) return true;
    // walls group covers regular walls + cannons + turrets + laser
    // cannons themselves (they're all added to walls). The other three
    // groups are checked individually because they're separate.
    const cx = (col + 0.5) * TILE_SIZE;
    const cy = (row + 0.5) * TILE_SIZE;
    const groupHasCell = (group: Phaser.Physics.Arcade.StaticGroup): boolean =>
      group.getChildren().some((child) => {
        const obj = child as Phaser.GameObjects.GameObject & { x?: number; y?: number };
        return obj.x === cx && obj.y === cy;
      });
    return (
      groupHasCell(this.walls) ||
      groupHasCell(this.glassWalls) ||
      groupHasCell(this.killableWalls) ||
      groupHasCell(this.keyWalls) ||
      // Hazards include the spike_block's full-cell lethal rect (centered
      // on the cell, so groupHasCell matches). Directional spikes also
      // live in hazards but their rects are off-center per SPIKE_LAYOUT,
      // so the cx/cy equality test naturally excludes them — the laser
      // passes over a directional spike's cell, only stopping at solids.
      groupHasCell(this.hazards)
    );
  };

  private buildLaserCannons(page: PageData): void {
    if (!page.laser_cannons) {
      return;
    }
    for (const lc of page.laser_cannons) {
      const laser = new LaserCannon(
        this,
        lc.x,
        lc.y,
        lc.dir,
        lc.rotate,
        lc.duration,
        lc.downtime,
        this.isLaserBlocker,
        this.gears,
        lc.delay ?? 0,
      );
      this.walls.add(laser);
      this.laserCannons.push(laser);
    }
  }

  private buildPortals(page: PageData): void {
    if (!page.portals) {
      return;
    }
    for (const pair of page.portals) {
      const built: Portal[] = [];
      for (const point of pair.points) {
        const portal = new Portal(this, point.x, point.y, pair.color);
        built.push(portal);
        this.portals.push(portal);
      }
      // Pair completion — only fully-formed (2-point) pairs are
      // functional; orphan singletons stay non-teleporting (partner null).
      if (built.length === 2) {
        built[0]!.partner = built[1]!;
        built[1]!.partner = built[0]!;
      }
    }
  }

  private buildGears(page: PageData): void {
    if (!page.gears) {
      return;
    }
    for (const g of page.gears) {
      const radiusPx = (g.size * TILE_SIZE) / 2;
      const speedPx = g.speed * TILE_SIZE;
      // Build the path in pixel coords. Index 0 is home, then each
      // waypoint cell-center.
      const path: Phaser.Math.Vector2[] = [
        new Phaser.Math.Vector2((g.x + 0.5) * TILE_SIZE, (g.y + 0.5) * TILE_SIZE),
      ];
      for (const wp of g.waypoints) {
        path.push(new Phaser.Math.Vector2((wp.x + 0.5) * TILE_SIZE, (wp.y + 0.5) * TILE_SIZE));
      }
      const gear = new Gear(
        this,
        path[0]!.x,
        path[0]!.y,
        radiusPx,
        speedPx,
        g.spin,
        path,
        g.closed,
      );
      this.gears.push(gear);
    }
  }

  // Decorative multi-cell text overlays. No body, no interaction —
  // pure visual. The cell-bounds outline drawn by the editor is
  // dropped at runtime; only the typed copy renders.
  private buildTextLabels(page: PageData): void {
    if (!page.text_labels) {
      return;
    }
    const pad = 4;
    for (const tl of page.text_labels) {
      if (tl.text.length === 0) continue;
      this.add.text(
        tl.x * TILE_SIZE + pad,
        tl.y * TILE_SIZE + pad,
        tl.text,
        {
          color: '#cccccc',
          fontSize: `${tl.font_size ?? 16}px`,
          fontFamily: 'system-ui, -apple-system, sans-serif',
          wordWrap: { width: tl.width * TILE_SIZE - pad * 2 },
        },
      );
    }
  }

  private buildTeleports(page: PageData): void {
    if (!page.teleports) {
      return;
    }
    for (const tp of page.teleports) {
      const x = (tp.x + 0.5) * TILE_SIZE;
      const y = (tp.y + 0.5) * TILE_SIZE;
      // No body — manual distance trigger in checkPageTriggers. Visual
      // is the 4-frame teleport animation. The destination page number
      // is editor-only context; in Play it's just visual noise, so no
      // overlay here.
      const sprite = this.add.sprite(x, y, 'teleport_0');
      sprite.setDisplaySize(TILE_SIZE, TILE_SIZE);
      sprite.play('teleport_spin');
      this.teleports.push({ x, y, targetPage: tp.target_page });
    }
  }

  // The level's exit is a single point; only renders if it's on this
  // page. Reaching it currently transitions back to page 0 (i.e.
  // restart the level); a future phase swaps that for a proper
  // "level complete" UX.
  private buildExit(): void {
    const exit = this.loadedLevel.exit;
    if (!exit || exit.page !== this.currentPageIndex) {
      return;
    }
    const x = (exit.x + 0.5) * TILE_SIZE;
    const y = (exit.y + 0.5) * TILE_SIZE;
    // Visual: same animated teleport ring as cross-page teleports, scaled
    // 1.5× so the exit reads as the "destination" of the level. Trigger
    // threshold (checkPageTriggers) is intentionally still TILE_SIZE — the
    // collision box doesn't grow with the visual.
    const visualSize = TILE_SIZE * 1.5;
    const ring = this.add.sprite(x, y, 'teleport_0');
    ring.setDisplaySize(visualSize, visualSize);
    ring.play('teleport_spin');
    this.add.text(x, y, 'EXIT', {
      color: '#ffd700',
      fontSize: '14px',
      fontStyle: 'bold',
    }).setOrigin(0.5);
    this.exit = { x, y, targetPage: 0 };
  }

  // AABB-style trigger check for teleports + exit (same pattern as
  // coins / keys). No-op while a transition is already in progress so
  // a single touch can't queue multiple page jumps.
  //
  // Two thresholds, intentionally different:
  //   - Teleports: 0.6 tile (slightly past half-cell — player CENTER
  //     just inside the cell counts, with a 20% leeway around the
  //     half-tile baseline so a near-miss flight that visually grazes
  //     the portal still triggers).
  //   - Exit: full-tile (player BODY must overlap the cell). The player
  //     is deliberately seeking the exit, so a 24-px dead zone on each
  //     side of the cell — where the player visually overlaps but the
  //     center-only check still fails — was just frustration.
  private checkPageTriggers(): void {
    if (this.transitioning) {
      return;
    }
    const px = this.player.x;
    const py = this.player.y;
    const teleportT = TILE_SIZE * 0.6;
    for (const tp of this.teleports) {
      if (Math.abs(tp.x - px) < teleportT && Math.abs(tp.y - py) < teleportT) {
        this.transitionToPage(tp.targetPage);
        return;
      }
    }
    const exitT = TILE_SIZE;
    if (this.exit && Math.abs(this.exit.x - px) < exitT && Math.abs(this.exit.y - py) < exitT) {
      this.handleExit();
    }
  }

  // End-of-level handler. Editor-launched runs return to EditScene on
  // the exit page (so the user can keep iterating). Standalone runs fade
  // to a "LEVEL COMPLETE" screen with a click-to-restart prompt — a
  // proper end-of-level UX is a future phase.
  //
  // Publish-verify runs: same return-to-editor path as a normal editor
  // test play, plus a one-shot `level-cleared` postMessage to the host
  // so the platform's playtest gate can mark the level cleared.
  private handleExit(): void {
    if (this.transitioning) return;
    this.transitioning = true;
    this.sound.play(SOUND_KEYS.levelCompleted, { volume: SOUND_VOLUMES.sfx });
    this.tweens.add({
      targets: this.fadeOverlay,
      alpha: 1,
      duration: FADE_DURATION_MS,
      onComplete: () => {
        if (this.publishVerify && this.embedLevelId) {
          postToParent('level-cleared', { levelId: this.embedLevelId });
        }
        if (this.launchedFromEditor) {
          this.scene.start('EditScene', {
            level: this.providedLevel ?? this.loadedLevel,
            pageIndex: this.loadedLevel.exit.page,
            levelPath: this.levelPath ?? undefined,
            dirty: this.editorDirty,
            embedLevelId: this.embedLevelId ?? undefined,
          });
        } else if (this.embedLevelId) {
          this.showEmbedComplete();
        } else if (this.campaignLevel > 0) {
          this.showCampaignComplete();
        } else {
          this.showLevelComplete();
        }
      },
    });
  }

  // Embedded-mode completion. Notifies the host (clear_count++) and
  // shows an in-game Replay button. The host owns "back to browse" /
  // "next level" navigation, so we don't render those.
  private showEmbedComplete(): void {
    if (this.embedLevelId) {
      postToParent('level-completed', { levelId: this.embedLevelId });
    }
    const dialog = document.createElement('div');
    dialog.className = 'editor-modal';
    dialog.setAttribute('data-modal-role', 'embed-complete');
    dialog.innerHTML = `
      <div class="editor-modal-backdrop"></div>
      <div class="editor-modal-content">
        <p>Level complete!</p>
        <div class="editor-modal-actions">
          <button data-modal-action="replay" class="editor-modal-btn editor-modal-btn-primary">Play again</button>
        </div>
      </div>
    `;
    document.body.appendChild(dialog);
    dialog.addEventListener('click', (ev) => {
      const t = ev.target as HTMLElement;
      if (t.getAttribute('data-modal-action') === 'replay') {
        this.sound.play(SOUND_KEYS.uiButton, { volume: SOUND_VOLUMES.sfx });
        dialog.remove();
        this.scene.restart({
          pageIndex: 0,
          level: this.providedLevel ?? this.loadedLevel,
          embedLevelId: this.embedLevelId ?? undefined,
        });
      }
    });
  }

  // Campaign-mode level-complete dialog. Two paths: continue to the
  // next numbered level, or return to MenuScene. The last campaign
  // level drops the Next button and shows a "game complete" headline
  // instead — the only action is back-to-menu.
  private showCampaignComplete(): void {
    const isLast = this.campaignLevel >= CAMPAIGN_LEVEL_COUNT;
    const headline = isLast
      ? `Congratulations! You cleared all ${CAMPAIGN_LEVEL_COUNT} levels.`
      : `Level ${this.campaignLevel} complete!`;
    const nextBtn = isLast
      ? ''
      : `<button data-modal-action="next" class="editor-modal-btn editor-modal-btn-primary">Next Level</button>`;
    const dialog = document.createElement('div');
    dialog.className = 'editor-modal';
    dialog.innerHTML = `
      <div class="editor-modal-backdrop"></div>
      <div class="editor-modal-content">
        <p>${headline}</p>
        <div class="editor-modal-actions">
          <button data-modal-action="menu" class="editor-modal-btn">Back to Menu</button>
          ${nextBtn}
        </div>
      </div>
    `;
    document.body.appendChild(dialog);
    dialog.addEventListener('click', (ev) => {
      const t = ev.target as HTMLElement;
      const action = t.getAttribute('data-modal-action');
      if (!action) return;
      this.sound.play(SOUND_KEYS.uiButton, { volume: SOUND_VOLUMES.sfx });
      if (action === 'menu') {
        dialog.remove();
        this.scene.start('MenuScene');
      } else if (action === 'next') {
        dialog.remove();
        this.scene.start('PlayScene', { campaignLevel: this.campaignLevel + 1 });
      }
    });
  }

  private showLevelComplete(): void {
    this.add
      .text(
        this.scale.gameSize.width / 2,
        this.scale.gameSize.height / 2 - 24,
        'LEVEL COMPLETE',
        {
          color: '#66d973',
          fontSize: '64px',
          fontStyle: 'bold',
          fontFamily: 'system-ui, sans-serif',
        },
      )
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(1001);
    this.add
      .text(
        this.scale.gameSize.width / 2,
        this.scale.gameSize.height / 2 + 36,
        'Click anywhere to restart',
        {
          color: '#cccccc',
          fontSize: '20px',
          fontFamily: 'system-ui, sans-serif',
        },
      )
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(1001);
    this.input.once('pointerdown', () => {
      this.scene.restart({ pageIndex: 0 });
    });
  }

  // Cross-page transition. Fades to black, then scene.restart with the
  // target page index — restart re-runs init/preload/create cleanly,
  // tearing down all entities for free. The new scene fades in via the
  // shouldFadeIn flag we pass through init data. If the level was
  // provided in-memory (editor hand-off), re-pass it so it survives the
  // restart instead of falling back to the on-disk file.
  private transitionToPage(targetPage: number): void {
    if (
      targetPage < 0 ||
      targetPage >= this.loadedLevel.pages.length ||
      this.transitioning
    ) {
      return;
    }
    this.transitioning = true;
    this.sound.play(SOUND_KEYS.teleport, { volume: SOUND_VOLUMES.sfx });
    this.tweens.add({
      targets: this.fadeOverlay,
      alpha: 1,
      duration: FADE_DURATION_MS,
      onComplete: () => {
        const restartData: {
          pageIndex: number;
          fadeIn: boolean;
          level?: LevelData;
          fromEditor?: boolean;
          levelPath?: string;
          editorDirty?: boolean;
          campaignLevel?: number;
          embedLevelId?: string;
          publishVerify?: boolean;
        } = { pageIndex: targetPage, fadeIn: true };
        if (this.providedLevel) restartData.level = this.providedLevel;
        if (this.launchedFromEditor) restartData.fromEditor = true;
        if (this.levelPath) restartData.levelPath = this.levelPath;
        if (this.editorDirty) restartData.editorDirty = this.editorDirty;
        if (this.campaignLevel) restartData.campaignLevel = this.campaignLevel;
        if (this.embedLevelId) restartData.embedLevelId = this.embedLevelId;
        if (this.publishVerify) restartData.publishVerify = true;
        this.scene.restart(restartData);
      },
    });
  }

  // Animations live on the global AnimationManager (scene.anims is
  // shared across scene restarts), so create them once and skip on
  // subsequent restarts.
  //
  // Player:
  //   player_roll_intro    — roll_0, roll_1                         (one-shot)
  //   player_roll_loop     — roll_2..5                              (∞ loop)
  //   player_ceiling_intro — player_idle, roll_0, roll_1            (one-shot)
  //   player_land          — roll_0, player_idle                    (one-shot;
  //                          last frame is the idle pose so the sprite
  //                          naturally rests on idle when the anim ends)
  // Pickups:
  //   coin_spin            — coin_0, coin_1                         (∞ loop)
  //   key_spin_<color>     — key_<color>_0..7  (one per color)      (∞ loop)
  // Portals / teleport:
  //   portal_spin_<color>  — portal_<color>_0..3  (one per color)   (∞ loop)
  //   teleport_spin        — teleport_0..3                          (∞ loop)
  // Conveyors:
  //   conveyor_<dir>_<piece> — 3 frames per (cw|ccw) × (left|middle|right)
  //
  // Player.syncAnimation chains the two intros into the loop; land is
  // standalone since it ends at idle.
  private ensureAnimations(): void {
    if (this.anims.exists('player_roll_loop')) return;

    this.anims.create({
      key: 'player_roll_intro',
      frames: [
        { key: 'player_roll_0' },
        { key: 'player_roll_1' },
      ],
      frameRate: 12,
      repeat: 0,
    });
    this.anims.create({
      key: 'player_roll_loop',
      frames: [
        { key: 'player_roll_2' },
        { key: 'player_roll_3' },
        { key: 'player_roll_4' },
        { key: 'player_roll_5' },
      ],
      frameRate: 12,
      repeat: -1,
    });
    this.anims.create({
      key: 'player_ceiling_intro',
      frames: [
        { key: 'player_idle' },
        { key: 'player_roll_0' },
        { key: 'player_roll_1' },
      ],
      frameRate: 12,
      repeat: 0,
    });
    this.anims.create({
      key: 'player_land',
      frames: [
        { key: 'player_roll_0' },
        { key: 'player_idle' },
      ],
      frameRate: 10,
      repeat: 0,
    });

    // Coin: 2-frame flicker, slow enough to read as a beacon rather
    // than visual noise.
    this.anims.create({
      key: 'coin_spin',
      frames: [
        { key: 'coin_0' },
        { key: 'coin_1' },
      ],
      frameRate: 6,
      repeat: -1,
    });
    // Keys: 8-frame rotation cycle, one animation per color so each
    // key sprite plays its own palette.
    for (let i = 0; i < 6; i++) {
      this.anims.create({
        key: `key_spin_${i}`,
        frames: Array.from({ length: 8 }, (_, f) => ({ key: `key_${i}_${f}` })),
        frameRate: 6,
        repeat: -1,
      });
    }
    // Portals: 4-frame loop per color, one animation per pair color.
    for (let c = 0; c < 6; c++) {
      this.anims.create({
        key: `portal_spin_${c}`,
        frames: Array.from({ length: 4 }, (_, f) => ({ key: `portal_${c}_${f}` })),
        frameRate: 8,
        repeat: -1,
      });
    }
    // Cross-page teleport: 4-frame loop, single color.
    this.anims.create({
      key: 'teleport_spin',
      frames: Array.from({ length: 4 }, (_, f) => ({ key: `teleport_${f}` })),
      frameRate: 8,
      repeat: -1,
    });
    // Conveyor: one looping anim per (direction, piece) combination.
    // All cells in a belt start playing simultaneously in
    // buildConveyors → buildConveyors runs in create(), so adjacent
    // pieces stay in sync visually.
    for (const dir of ['cw', 'ccw'] as const) {
      for (const piece of ['left', 'middle', 'right'] as const) {
        this.anims.create({
          key: `conveyor_${dir}_${piece}`,
          frames: [
            { key: `conveyor_${dir}_${piece}_0` },
            { key: `conveyor_${dir}_${piece}_1` },
            { key: `conveyor_${dir}_${piece}_2` },
          ],
          frameRate: 8,
          repeat: -1,
        });
      }
    }
  }

  // Full-viewport background. Pinned to the camera (scrollFactor 0) so
  // it stays fixed regardless of where the camera scrolls to center the
  // current room. Sits beneath the grid lines (depth -200 < grid -100).
  private drawBackground(): void {
    const w = this.scale.gameSize.width;
    const h = this.scale.gameSize.height;
    this.add
      .image(w / 2, h / 2, 'background')
      .setDisplaySize(w, h)
      .setScrollFactor(0)
      .setDepth(-200);
  }

  private drawGridBackground(cols: number, rows: number): void {
    const gfx = this.add.graphics();
    gfx.lineStyle(1, COLOR_GRID, 0.6);
    const w = cols * TILE_SIZE;
    const h = rows * TILE_SIZE;
    for (let x = 0; x <= w; x += TILE_SIZE) {
      gfx.lineBetween(x, 0, x, h);
    }
    for (let y = 0; y <= h; y += TILE_SIZE) {
      gfx.lineBetween(0, y, w, y);
    }
    gfx.setDepth(-100);
  }
}
